import { Turnstile, TurnstileInstance } from "@marsidev/react-turnstile";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from "react";

type TurnstileContextValue = {
  /**
   * Runs the (invisible) Turnstile challenge and resolves with a single-use
   * token to send to the backend. Resolves with `null` if the widget is not
   * configured/ready or the challenge fails. Drop-in replacement for
   * reCAPTCHA v3's `executeRecaptcha`.
   */
  executeTurnstile: () => Promise<string | null>;
};

const TurnstileContext = createContext<TurnstileContextValue>({
  executeTurnstile: async () => null,
});

export function useTurnstile() {
  return useContext(TurnstileContext);
}

type Props = {
  siteKey: string | undefined;
  children: ReactNode;
};

export default function TurnstileProvider({ siteKey, children }: Props) {
  const widgetRef = useRef<TurnstileInstance | null>(null);

  const executeTurnstile = useCallback(async () => {
    const widget = widgetRef.current;
    if (!widget) {
      console.log("Turnstile widget not ready");
      return null;
    }
    // Tokens are single-use, so reset before each run to clear any prior token.
    widget.reset();
    widget.execute();
    try {
      // Bound the wait so a widget/network failure surfaces in ~15s instead of
      // freezing the submit button on the library's 30s default.
      return await widget.getResponsePromise(15000);
    } catch (error) {
      console.error("Turnstile challenge failed", error);
      return null;
    }
  }, []);

  return (
    <TurnstileContext.Provider value={{ executeTurnstile }}>
      {children}
      {siteKey && (
        <Turnstile
          ref={widgetRef}
          siteKey={siteKey}
          options={{ execution: "execute", appearance: "interaction-only" }}
        />
      )}
    </TurnstileContext.Provider>
  );
}
